/**
 * DeleteAlertDialog (Story 27.2) — §4.4 confirmation Dialog (no type-to-confirm:
 * an alert is a lightweight rule, not a cascade of client data). Same Dialog
 * scaffold as DeleteClientDialog (Radix dialog, bg-black/35 overlay, rounded-3xl
 * p-8, motion enter/exit) per the design-system directive §4.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { motion, AnimatePresence } from "motion/react";
import { Button } from "@provenleap/ui";
import { Trash } from "@phosphor-icons/react";
import { apiDelete } from "../../lib/apiClient";
import type { ToastType } from "../../hooks/useToast";
import type { Alert } from "./types";

interface DeleteAlertDialogProps {
  alert: Alert | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDeleted: () => void;
  showToast: (type: ToastType, message: string) => void;
}

export function DeleteAlertDialog({
  alert,
  open,
  onOpenChange,
  onDeleted,
  showToast,
}: DeleteAlertDialogProps) {
  const queryClient = useQueryClient();

  const deleteAlert = useMutation({
    mutationFn: async () => {
      if (!alert) throw new Error("No alert selected");
      const res = await apiDelete(`/api/alerts/${alert.id}`);
      if (!res.ok && res.status !== 404) {
        const json = await res.json().catch(() => ({ detail: "Server error" }));
        throw { status: res.status, ...json };
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["alerts"] });
      onDeleted();
    },
    onError: (err: any) => {
      showToast("error", err?.title ?? err?.detail ?? "Failed to delete alert");
    },
  });

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <AnimatePresence>
        {open && alert && (
          <DialogPrimitive.Portal forceMount>
            <DialogPrimitive.Overlay asChild forceMount>
              <motion.div
                className="fixed inset-0 z-50 bg-black/35"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              />
            </DialogPrimitive.Overlay>

            <DialogPrimitive.Content asChild forceMount>
              <motion.div
                className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-3xl border-[0.5px] border-input bg-background p-8 shadow-lg outline-none"
                initial={{ opacity: 0, scale: 0.95, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 10 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              >
                <div className="flex flex-col space-y-3 text-left">
                  <DialogPrimitive.Title className="text-lg font-semibold leading-none tracking-tight">
                    Delete {alert.name}?
                  </DialogPrimitive.Title>
                  <DialogPrimitive.Description className="text-sm text-muted-foreground">
                    This alert will be permanently removed and will stop
                    evaluating. This action cannot be undone.
                  </DialogPrimitive.Description>
                </div>

                <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 mt-6">
                  <Button
                    variant="ghost"
                    className="rounded-2xl bg-background cursor-pointer shadow-xs hover:-translate-y-0.5 hover:shadow-sm hover:bg-background transition-all"
                    style={{ border: "0.5px solid hsl(var(--input))" }}
                    onClick={() => onOpenChange(false)}
                    disabled={deleteAlert.isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    className="rounded-2xl cursor-pointer"
                    onClick={() => deleteAlert.mutate()}
                    disabled={deleteAlert.isPending}
                    data-testid="confirm-delete-alert-btn"
                  >
                    <Trash size={16} className="mr-1" />
                    {deleteAlert.isPending ? "Deleting..." : "Delete"}
                  </Button>
                </div>
              </motion.div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        )}
      </AnimatePresence>
    </DialogPrimitive.Root>
  );
}
